// ============================================================================
//  NhisImports.js — admin dialog for uploading the three NHIS release PDFs.
// ----------------------------------------------------------------------------
//  Whenever NHIS publishes a new release (tariff manual, medicines list, or
//  a level-specific tariff booklet), an admin drops the PDFs here, gives the
//  release a label ("2025 Update"), previews the counts, and commits.
//
//  The backend spawns the existing Python parsers in scripts/, so no manual
//  CLI step is needed. Preview never touches the DB. Commit runs one atomic
//  transaction across whichever files were uploaded.
// ============================================================================

import { useRef, useState } from 'react';
import axios from 'axios';
import notify from './notify';
import { adminNhisImportPreview, adminNhisImportCommit } from '../BaseURL';
import { AdminHeader, AdminCard } from './AdminSection';
import './Admin.css';

const FILE_KINDS = [
  {
    key: 'tariff',
    label: 'Tariff & Operational Manual',
    hint: 'The Annex-C PDF with G-DRGs, procedures, diagnoses, and facility levels.',
    accept: '.pdf',
  },
  {
    key: 'medicines',
    label: 'Medicines List',
    hint: 'The NHIS Medicines List PDF with prescribing levels and prices.',
    accept: '.pdf',
  },
  {
    key: 'prices',
    label: 'Level-specific Tariff Booklet',
    hint: 'Per-level tariff prices (e.g. "Public Primary Hospital Tariff.pdf"). Needs a hospital-level name and month.',
    accept: '.pdf',
  },
];

export default function NhisImports() {
  const [files, setFiles] = useState({ tariff: null, medicines: null, prices: null });
  const [releaseLabel, setReleaseLabel] = useState('');
  // Only relevant when the "prices" file is picked — the parser needs these.
  const [level, setLevel] = useState('');
  const [monthYear, setMonthYear] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const inputRefs = useRef({ tariff: null, medicines: null, prices: null });

  const hasAnyFile = Boolean(files.tariff || files.medicines || files.prices);
  const canSubmitPrices = files.prices ? (level.trim() && monthYear.trim()) : true;

  const onPick = (kind) => (e) => {
    const f = e.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [kind]: f }));
    setPreview(null);
  };

  const clearFile = (kind) => {
    setFiles((prev) => ({ ...prev, [kind]: null }));
    if (inputRefs.current[kind]) inputRefs.current[kind].value = '';
    setPreview(null);
  };

  const buildFormData = () => {
    const fd = new FormData();
    if (files.tariff)    fd.append('tariff',    files.tariff);
    if (files.medicines) fd.append('medicines', files.medicines);
    if (files.prices)    fd.append('prices',    files.prices);
    if (releaseLabel.trim()) fd.append('releaseLabel', releaseLabel.trim());
    if (files.prices) {
      if (level.trim())     fd.append('level',     level.trim());
      if (monthYear.trim()) fd.append('monthYear', monthYear.trim());
    }
    return fd;
  };

  const doPreview = async () => {
    if (!hasAnyFile) return notify.error('Nothing to preview', 'Pick at least one PDF.');
    if (!canSubmitPrices) return notify.error('Missing details', 'The prices booklet needs a level name and month.');
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await axios.post(adminNhisImportPreview, buildFormData(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (err) {
      notify.error('Preview failed', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    if (!preview) return notify.error('Preview first', 'Run a preview before committing.');
    setCommitting(true);
    try {
      const { data } = await axios.post(adminNhisImportCommit, buildFormData(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      notify.success(
        'Import complete',
        `Release "${data.summary.releaseLabel || releaseLabel || 'Unlabelled'}" written to the catalogue.`
      );
      setPreview({ ...preview, committed: data.summary });
    } catch (err) {
      notify.error('Import failed', err?.response?.data?.error || err.message);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <>
      <AdminHeader
        title="NHIS Release Importer"
        description="Upload the NHIS release PDFs (tariff manual, medicines list, level-specific tariff) and the system rebuilds the catalogue automatically."
      />

      <AdminCard>
        <div style={{ padding: 20, display: 'grid', gap: 20 }}>
          <div>
            <label style={labelStyle}>Release label</label>
            <input
              type="text"
              placeholder="e.g. 2025 Update"
              value={releaseLabel}
              onChange={(e) => setReleaseLabel(e.target.value)}
              style={inputStyle}
            />
            <div style={hintStyle}>
              Used to tag the medicines version. Tariff-master rows and prices use their own natural keys.
            </div>
          </div>

          {FILE_KINDS.map((k) => (
            <FileSlot
              key={k.key}
              kind={k}
              file={files[k.key]}
              onPick={onPick(k.key)}
              onClear={() => clearFile(k.key)}
              inputRef={(el) => { inputRefs.current[k.key] = el; }}
            />
          ))}

          {files.prices && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Prices booklet — hospital level *</label>
                <input
                  type="text"
                  placeholder='e.g. "Public Primary Hospital (Catering Exclusive)"'
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Month & year *</label>
                <input
                  type="text"
                  placeholder="e.g. Feb 2023"
                  value={monthYear}
                  onChange={(e) => setMonthYear(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              className="btn btn-ghost"
              onClick={doPreview}
              disabled={!hasAnyFile || loading || committing}
            >
              {loading ? 'Parsing…' : 'Preview'}
            </button>
            <button
              className="btn btn-primary"
              onClick={doCommit}
              disabled={!preview || committing || preview?.committed}
            >
              {committing ? 'Importing…' : preview?.committed ? 'Imported' : 'Commit to catalogue'}
            </button>
          </div>
        </div>
      </AdminCard>

      {preview && <PreviewPanel preview={preview} />}
    </>
  );
}

function FileSlot({ kind, file, onPick, onClear, inputRef }) {
  return (
    <div style={slotStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700 }}>{kind.label}</div>
          <div style={hintStyle}>{kind.hint}</div>
        </div>
        {file && (
          <button className="btn btn-ghost btn-xs" onClick={onClear}>Clear</button>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        {file ? (
          <div style={fileChipStyle}>
            <span>📄</span>
            <span style={{ fontWeight: 600 }}>{file.name}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
              {(file.size / 1024).toFixed(0)} KB
            </span>
          </div>
        ) : (
          <label style={pickerStyle}>
            <input
              ref={inputRef}
              type="file"
              accept={kind.accept}
              onChange={onPick}
              style={{ display: 'none' }}
            />
            <span>Browse for {kind.accept.toUpperCase()}…</span>
          </label>
        )}
      </div>
    </div>
  );
}

function PreviewPanel({ preview }) {
  const p = preview.previews || {};
  const c = preview.committed;
  return (
    <AdminCard>
      <div style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>{c ? 'Import summary' : 'Preview'}</h3>
        <table className="svc-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>File</th>
              <th>Rows detected</th>
              <th>{c ? 'Applied' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            {p.tariff && (
              <tr>
                <td>
                  <strong>Tariff Manual</strong>
                  <div style={hintStyle}>{p.tariff.filename}</div>
                </td>
                <td>{p.tariff.gdrgs} G-DRGs · {p.tariff.diagnoses} diagnoses · {p.tariff.procedures} procedures</td>
                <td>
                  {c?.tariff
                    ? `+${c.tariff.gdrgsInserted} new, ${c.tariff.gdrgsUpdated} updated, +${c.tariff.diagnosesInserted} diagnoses`
                    : 'Upserts by G-DRG code'}
                </td>
              </tr>
            )}
            {p.medicines && (
              <tr>
                <td>
                  <strong>Medicines List</strong>
                  <div style={hintStyle}>{p.medicines.filename}</div>
                </td>
                <td>{p.medicines.rows} medicines</td>
                <td>
                  {c?.medicines
                    ? `+${c.medicines.inserted} new, ${c.medicines.updated} updated · active version "${c.medicines.versionLabel}"`
                    : `Version → ${p.medicines.versionLabel}`}
                </td>
              </tr>
            )}
            {p.prices && (
              <tr>
                <td>
                  <strong>Tariff Prices</strong>
                  <div style={hintStyle}>{p.prices.filename}</div>
                </td>
                <td>{p.prices.rows} rows</td>
                <td>
                  {c?.prices
                    ? `+${c.prices.insertedServices + c.prices.insertedInvestigations} new, ` +
                      `${c.prices.updatedServices + c.prices.updatedInvestigations} updated`
                    : `${p.prices.monthYear} · ${p.prices.level}`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}

// ─── Inline styles kept local so this component doesn't need its own CSS. ───
const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 };
const hintStyle  = { fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' };
const slotStyle  = { padding: 14, border: '1px dashed var(--border)', borderRadius: 10, background: 'var(--surface-muted, transparent)' };
const pickerStyle = { display: 'inline-flex', alignItems: 'center', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600 };
const fileChipStyle = { display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(34, 197, 94, 0.10)', border: '1px solid rgba(34,197,94,0.35)' };
